import { Platform } from 'react-native';

const EMBED_MESSAGE_SOURCE = 'bookapt-embed';

export const WIDGET_BOOKING_API = 'https://bookapt-payment-api.onrender.com/api';

export type WidgetEmbedMessageType = 'close' | 'booking-complete';

export type WidgetBookingUrlParams = {
  businessId: string;
  serviceId?: string;
};

function readUrlSearchParams(): URLSearchParams | null {
  if (Platform.OS !== 'web') {
    return null;
  }
  try {
    const win = globalThis as typeof globalThis & { location?: { search?: string } };
    const search = win.location?.search || '';
    if (!search) {
      return null;
    }
    return new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  } catch {
    return null;
  }
}

export function getWidgetBookingParamsFromUrl(): WidgetBookingUrlParams | null {
  const params = readUrlSearchParams();
  if (!params) {
    return null;
  }
  const widgetBook = params.get('widgetBook');
  const widgetOk = widgetBook === '1' || widgetBook === 'true';
  const businessId =
    params.get('businessId') || params.get('businessid') || '';
  if (!widgetOk || !businessId) {
    return null;
  }
  const serviceId =
    params.get('serviceId') || params.get('serviceid') || undefined;
  return serviceId ? { businessId, serviceId } : { businessId };
}

const WEBAPP_INITIAL_TAB_KEY = 'bookapt_initial_tab';

export function isInIframe(): boolean {
  if (Platform.OS !== 'web') {
    return false;
  }
  try {
    const win = globalThis as typeof globalThis & {
      self?: typeof globalThis;
      top?: typeof globalThis;
    };
    return !!win.self && !!win.top && win.self !== win.top;
  } catch {
    // Cross-origin iframe — assume embedded.
    return true;
  }
}

/** True when booking UI should run as a merchant-site embed (iframe overlay). */
export function isWidgetEmbedContext(): boolean {
  return isWidgetEmbedMode() || isInIframe();
}

export function isWidgetEmbedBookingEntry(): boolean {
  const params = getWidgetBookingParamsFromUrl();
  if (!params) {
    return false;
  }
  // Legacy widgets use ?widgetBook=1&businessId= without embed=1; iframe implies embed.
  return isWidgetEmbedContext();
}

export function isWidgetEmbedMode(): boolean {
  if (Platform.OS !== 'web') {
    return false;
  }
  try {
    const win = globalThis as typeof globalThis & { location?: { search?: string } };
    const search = win.location?.search || '';
    if (!search) {
      return false;
    }
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    const embed = params.get('embed');
    return embed === '1' || embed === 'true';
  } catch {
    return false;
  }
}

export function notifyWidgetEmbedParent(
  type: WidgetEmbedMessageType,
  payload?: Record<string, unknown>,
): void {
  if (Platform.OS !== 'web' || !isWidgetEmbedContext()) {
    return;
  }
  try {
    const win = globalThis as typeof globalThis & {
      parent?: { postMessage?: (data: unknown, targetOrigin: string) => void };
    };
    if (!win.parent || win.parent === win) {
      return;
    }
    win.parent.postMessage(
      {
        source: EMBED_MESSAGE_SOURCE,
        type,
        ...(payload || {}),
      },
      '*',
    );
  } catch {
    // ignore cross-origin errors
  }
}

/** After widget booking in a standalone tab (not merchant iframe), open the main web app on Appointments. */
export function navigateToWebAppAfterWidgetBooking(): void {
  if (Platform.OS !== 'web') {
    return;
  }
  try {
    const storage = (globalThis as typeof globalThis & { sessionStorage?: Storage })
      .sessionStorage;
    storage?.setItem(WEBAPP_INITIAL_TAB_KEY, 'appointments');
    const win = globalThis as typeof globalThis & {
      location?: { origin?: string; pathname?: string; href?: string };
    };
    const origin = win.location?.origin;
    if (!origin) {
      return;
    }
    const pathname = win.location?.pathname || '/app.html';
    const appPath = pathname.includes('app.html')
      ? pathname.replace(/app\.html.*$/, 'app.html')
      : `${pathname.replace(/\/?$/, '')}/app.html`;
    win.location!.href = `${origin}${appPath}?tab=appointments`;
  } catch {
    // ignore
  }
}

export function consumeWebAppInitialTab(): 'appointments' | 'dashboard' | null {
  if (Platform.OS !== 'web') {
    return null;
  }
  try {
    const win = globalThis as typeof globalThis & { location?: { search?: string } };
    const search = win.location?.search || '';
    if (search) {
      const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
      const tabParam = params.get('tab') || params.get('initialTab');
      if (tabParam === 'appointments' || tabParam === 'dashboard') {
        return tabParam;
      }
    }
  } catch {
    // ignore
  }
  try {
    const storage = (globalThis as typeof globalThis & { sessionStorage?: Storage })
      .sessionStorage;
    const tab = storage?.getItem(WEBAPP_INITIAL_TAB_KEY);
    if (tab === 'appointments' || tab === 'dashboard') {
      storage?.removeItem(WEBAPP_INITIAL_TAB_KEY);
      return tab;
    }
  } catch {
    // ignore
  }
  return null;
}

/** Skip duplicate success alerts when the merchant-site iframe overlay is about to close. */
export function shouldSuppressWidgetEmbedSuccessAlert(): boolean {
  return isInIframe() && isWidgetEmbedContext();
}

/** Payload sent to the merchant page when a widget embed booking finishes. */
export type WidgetProductPick = {
  quantity: number;
  selectedOptions: Record<string, string>;
};

const WIDGET_PRODUCT_PICK_PREFIX = 'bookapt_widget_pick_';

export function widgetProductPickStorageKey(
  businessId: string,
  serviceId: string,
): string {
  return `${WIDGET_PRODUCT_PICK_PREFIX}${businessId}_${serviceId}`;
}

/** Read and clear product options chosen in the merchant-site widget before embed opens. */
export function consumeWidgetProductPick(
  businessId: string,
  serviceId: string,
): WidgetProductPick | null {
  if (Platform.OS !== 'web') {
    return null;
  }
  try {
    const storage = (globalThis as typeof globalThis & { sessionStorage?: Storage })
      .sessionStorage;
    if (!storage) {
      return null;
    }
    const key = widgetProductPickStorageKey(businessId, serviceId);
    const raw = storage.getItem(key);
    storage.removeItem(key);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as WidgetProductPick;
    if (
      !parsed ||
      typeof parsed.quantity !== 'number' ||
      parsed.quantity < 1 ||
      !parsed.selectedOptions ||
      typeof parsed.selectedOptions !== 'object'
    ) {
      return null;
    }
    return {
      quantity: Math.floor(parsed.quantity),
      selectedOptions: parsed.selectedOptions,
    };
  } catch {
    return null;
  }
}

export function buildWidgetEmbedBookingCompletePayload(booking: {
  id: string;
  businessId: string;
  serviceName?: string;
  appointmentDate?: string;
  appointmentTime?: string;
  customerData?: { email?: string; name?: string };
  businessData?: { name?: string };
}): Record<string, unknown> {
  const emailSentAt = new Date().toLocaleString();
  return {
    appointmentId: booking.id,
    businessId: booking.businessId,
    serviceName: booking.serviceName,
    appointmentDate: booking.appointmentDate,
    appointmentTime: booking.appointmentTime,
    customerEmail: booking.customerData?.email || '',
    customerName: booking.customerData?.name || '',
    businessName: booking.businessData?.name || '',
    emailSentAt,
  };
}
